/**
 * src/shell/auth/services/TeardownSemaphore.ts
 *
 * Coordinates safe asynchronous teardown during fast logout (<16ms dispatch).
 *
 * Guardrails:
 * 1. Immediate synchronous state wipe (<16ms).
 * 2. Hard 2-second timeout: forcibly severs pending connections via AbortController.
 * 3. Silence guard: silences listeners before invoking teardown to prevent unmounted state errors.
 * 4. Blocks new login attempts while draining until semaphore is released.
 */

export type TeardownHook = (signal: AbortSignal) => Promise<unknown>;

export class TeardownSemaphore {
  private static instance: TeardownSemaphore;
  private isDrainingState = false;
  private drainPromise: Promise<void> | null = null;
  private drainResolver: (() => void) | null = null;
  private teardownHooks: Set<TeardownHook> = new Set();

  private constructor() {}

  public static getInstance(): TeardownSemaphore {
    if (!TeardownSemaphore.instance) {
      TeardownSemaphore.instance = new TeardownSemaphore();
    }
    return TeardownSemaphore.instance;
  }

  /**
   * Register a background cleanup hook (WebRTC peer disconnect, WebSocket close, IndexedDB flush).
   */
  public registerHook(hook: TeardownHook): () => void {
    this.teardownHooks.add(hook);
    return () => {
      this.teardownHooks.delete(hook);
    };
  }

  public isDraining(): boolean {
    return this.isDrainingState;
  }

  /**
   * Blocks execution until any ongoing teardown completes or hits the 2s timeout.
   */
  public async acquire(): Promise<() => void> {
    if (this.isDrainingState && this.drainPromise) {
      await this.drainPromise;
    }
    return () => this.release();
  }

  public release(): void {
    if (this.drainResolver) {
      this.drainResolver();
      this.drainResolver = null;
    }
    this.drainPromise = null;
    this.isDrainingState = false;
  }

  /**
   * Executes the rapid logout lifecycle with performance marks and hard abort.
   */
  public async executeTeardown(onSynchronousWipe: () => void): Promise<void> {
    // 1. Performance mark for dispatch
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark('auth:logout_dispatch_start');
    }

    // 2. Synchronous in-memory wipe (<16ms)
    onSynchronousWipe();

    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark('auth:vault_cleared');
    }

    // 3. Mark semaphore as draining
    this.isDrainingState = true;
    this.drainPromise = new Promise<void>((resolve) => {
      this.drainResolver = resolve;
    });

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      // Hard abort kill on 2-second timeout
      abortController.abort();
    }, 2000);

    try {
      // 4. Run all registered teardown hooks in parallel with silence guard & allSettled
      const hookPromises = Array.from(this.teardownHooks).map(async (hook) => {
        try {
          await hook(abortController.signal);
        } catch {
          // Teardown hook failures are caught and silenced
        }
      });

      await Promise.race([
        Promise.allSettled(hookPromises),
        new Promise((resolve) => {
          abortController.signal.addEventListener('abort', resolve, { once: true });
        }),
      ]);
    } finally {
      clearTimeout(timeoutId);
      this.release();
      if (typeof performance !== 'undefined' && performance.mark) {
        performance.mark('auth:teardown_complete');
      }
    }
  }
}

export const teardownSemaphore = TeardownSemaphore.getInstance();
